import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Eye, Flag, CheckCircle2, Plus } from 'lucide-react';
import { useApi } from '@/contexts/AuthContext';

const C = { navy: '#0D1B2A', slate: '#5C6B7A', green: '#1B7A3D', amber: '#8A6D1D', red: '#B0413E', line: '#E4E9EF' };
const STATUS = { current: { bg: '#E8F4EC', fg: C.green }, stale: { bg: '#FDF6E3', fg: C.amber }, flagged: { bg: '#FBEBEA', fg: C.red } };
const TYPES = ['regulator', 'afca', 'legislation', 'carrier_guide', 'wording', 'market_intel', 'internal'];

function SourceRow({ s, api, onDone }) {
  const [note, setNote] = useState('');
  const st = STATUS[s.effective_status] || STATUS.current;

  const flag = async () => {
    if (note.trim().length < 5) return toast.error('A flag needs a note (min 5 chars)');
    try {
      await api.post(`/dean/sources/${s.source_id}/flag`, { note: note.trim() });
      toast.success(`${s.name} flagged`); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Flag failed'); }
  };
  const verify = async () => {
    try {
      await api.post(`/dean/sources/${s.source_id}/verify`);
      toast.success(`${s.name} verified current`); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Verify failed'); }
  };

  return (
    <div className="px-4 py-3 border-b last:border-b-0" style={{ borderColor: C.line }} data-testid={`source-row-${s.source_id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm font-semibold" style={{ color: C.navy }}>{s.name}</span>
        <span className="text-[10px] px-1.5 py-0.5 rounded uppercase font-semibold" style={{ background: '#EEF1F5', color: C.slate }}>{s.type}</span>
        {(s.layers || []).map(l => <span key={l} className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: '#EAF1FB', color: '#2A6FC4' }}>{l}</span>)}
        <span className="text-[10px] font-bold px-2 py-0.5 rounded uppercase" style={{ background: st.bg, color: st.fg }} data-testid={`source-status-${s.source_id}`}>{s.effective_status}</span>
        <span className="ml-auto text-[11px]" style={{ color: C.slate }}>
          verified {(s.last_verified_at || '').slice(0, 10)} · cycle {s.review_cycle_days}d{s.verify_due ? ` · due ${s.verify_due.slice(0, 10)}` : ''}
        </span>
      </div>
      <div className="mt-1 text-[11px]" style={{ color: C.slate }}>{s.ref}{s.note ? ` — ${s.note}` : ''}</div>
      {s.flag_note && <div className="mt-1 text-[11px] italic" style={{ color: C.red }}>Flagged by {s.flagged_by}: {s.flag_note}</div>}
      <div className="mt-2 flex items-center gap-2">
        <input value={note} onChange={e => setNote(e.target.value)} placeholder="flag note (why is this source stale/wrong?)"
          className="w-72 border rounded-sm px-2 py-1 text-[11px]" style={{ borderColor: C.line }} data-testid={`source-note-${s.source_id}`} />
        <button onClick={flag} className="flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-semibold border" style={{ borderColor: C.red, color: C.red }} data-testid={`source-flag-${s.source_id}`}>
          <Flag size={11} /> Flag
        </button>
        <button onClick={verify} className="flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-semibold border" style={{ borderColor: C.green, color: C.green }} data-testid={`source-verify-${s.source_id}`}>
          <CheckCircle2 size={11} /> Verify current
        </button>
      </div>
    </div>
  );
}

export default function SourceWatcherPage() {
  const api = useApi();
  const [data, setData] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', type: 'carrier_guide', ref: '', review_cycle_days: 90 });

  const load = useCallback(async () => {
    try { const r = await api.get('/dean/sources'); setData(r.data); } catch { /* noop */ }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (form.name.length < 3) return toast.error('Name required');
    try {
      await api.post('/dean/sources', { ...form, review_cycle_days: Number(form.review_cycle_days) || 90 });
      toast.success('Source registered'); setShowAdd(false);
      setForm({ name: '', type: 'carrier_guide', ref: '', review_cycle_days: 90 }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Add failed'); }
  };

  if (!data) return <div className="p-4 text-xs" style={{ color: C.slate }}>Loading source register…</div>;

  return (
    <div className="space-y-4" data-testid="source-watcher-page">
      <div className="flex flex-wrap items-center gap-3">
        <Eye size={15} style={{ color: C.navy }} />
        <span className="text-xs" style={{ color: C.slate }}>
          Dean's source watcher (minimal): every source feeding the knowledge layer, its vintage and review cycle. Staleness is computed, flags are human. Gap report follows in Week 3.
        </span>
        {data.stale > 0 && <span className="text-xs px-2 py-0.5 rounded font-semibold" style={{ background: '#FDF6E3', color: C.amber }} data-testid="sources-stale-count">{data.stale} stale</span>}
        {data.flagged > 0 && <span className="text-xs px-2 py-0.5 rounded font-semibold" style={{ background: '#FBEBEA', color: C.red }} data-testid="sources-flagged-count">{data.flagged} flagged</span>}
        <button onClick={() => setShowAdd(!showAdd)} className="ml-auto flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold text-white" style={{ background: C.navy }} data-testid="add-source-btn">
          <Plus size={12} /> Register source
        </button>
      </div>

      {showAdd && (
        <div className="p-4 rounded-lg border flex flex-wrap items-end gap-3" style={{ borderColor: C.line, background: '#fff' }} data-testid="add-source-form">
          <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Source name" className="border rounded-sm px-2 py-1.5 text-xs w-56" style={{ borderColor: C.line }} data-testid="source-name-input" />
          <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} className="border rounded-sm px-2 py-1.5 text-xs" style={{ borderColor: C.line }} data-testid="source-type-select">
            {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <input value={form.ref} onChange={e => setForm(f => ({ ...f, ref: e.target.value }))} placeholder="Reference / URL" className="border rounded-sm px-2 py-1.5 text-xs w-64" style={{ borderColor: C.line }} data-testid="source-ref-input" />
          <input type="number" value={form.review_cycle_days} onChange={e => setForm(f => ({ ...f, review_cycle_days: e.target.value }))} className="border rounded-sm px-2 py-1.5 text-xs w-24" style={{ borderColor: C.line }} title="Review cycle (days)" data-testid="source-cycle-input" />
          <button onClick={add} className="px-3 py-1.5 rounded-md text-xs font-semibold text-white" style={{ background: C.green }} data-testid="source-add-submit-btn">Register</button>
        </div>
      )}

      <div className="rounded-lg border overflow-hidden" style={{ borderColor: C.line, background: '#fff' }}>
        {data.sources.map(s => <SourceRow key={s.source_id} s={s} api={api} onDone={load} />)}
      </div>
    </div>
  );
}
